import React, {useEffect, useMemo, useState} from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme
} from "@mui/material";
import {useQueryClient} from "@tanstack/react-query";

import {DefaultTableContainer} from "src/components/common/DefaultTable";
import {DefaultButton} from "src/components/common/DefaultButton";
import {useErrorDialog} from "src/components/common/Dialog/ErrorDialog";
import {useMutationWithAccessToken} from "src/hooks/TanstackHooks";
import {usePortalContext} from "src/hooks/PortalContext";
import {setClientSourceOfTruth} from "src/services/Clients/ClientsQueries";
import {
  isClientInfoQueryKey,
  isSourceOfTruthDataQueryKey
} from "src/services/shared/serviceConstants";

export const automaticLabel = 'Automatic';

// Select value standing for "no source of truth set" (saas null)
export const automaticValue = '';

type SaasOption = {identifier: string, name: string};

// One option per connected SaaS, however many connections it has
export const sourceOfTruthOptions = (
  connectedSaases: {identifier?: string, name?: string}[]
): SaasOption[] => {
  const byIdentifier = new Map<string, SaasOption>();
  connectedSaases.forEach(({identifier, name}) => {
    if (identifier && !byIdentifier.has(identifier)) {
      byIdentifier.set(identifier, {identifier, name: name ?? identifier});
    }
  });
  return Array.from(byIdentifier.values())
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Lets any user with access to the selected client choose the SaaS whose
// accounts define who should exist (ghost users are users without one),
// or leave it automatic.
export const SourceOfTruthControl = ({
  connectedSaases,
}: {
  connectedSaases: {identifier?: string, name?: string}[],
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const {clientInfo} = usePortalContext();

  const current: SaasOption | null = clientInfo.sourceOfTruth ?? null;
  const currentValue = current?.identifier ?? automaticValue;
  const currentLabel = current?.name ?? automaticLabel;

  const [selected, setSelected] = useState(currentValue);

  // Follow the saved value, e.g. once the client info is fetched again
  useEffect(() => {
    setSelected(currentValue);
  }, [currentValue]);

  // The current source of truth stays selectable even when the client
  // no longer has a connection to it
  const options = useMemo(() => {
    const connected = sourceOfTruthOptions(connectedSaases);
    return current && !connected.some((o) => o.identifier === current.identifier) ?
      [...connected, current] :
      connected;
  }, [connectedSaases, current]);

  const {
    dialog: errorDialog,
    setIsOpen: setIsErrorDialogOpen,
    setErrorMessage,
  } = useErrorDialog({
    onDismiss: () => {},
  });

  const {
    mutate: doSetSourceOfTruth,
    isPending: isSaving,
  } = useMutationWithAccessToken({
    mutation: setClientSourceOfTruth,
    tanstackOptions: {
      onSuccess: async () => {
        // Fetch the logged-in user's clients again, which the portal
        // context's clientInfo.sourceOfTruth comes from...
        await queryClient.invalidateQueries({
          predicate: ({queryKey}) => isClientInfoQueryKey(queryKey),
        });
        // ...then have the data computed against the source of truth
        // fetched again when next shown, with the new source of truth
        await queryClient.invalidateQueries({
          predicate: ({queryKey}) => isSourceOfTruthDataQueryKey(queryKey),
          refetchType: 'none',
        });
      },
      onError: (error: { response?: { errors?: { message: string }[] } }) => {
        setErrorMessage(error.response?.errors?.[0]?.message ?? 'Could not change the source of truth');
        setIsErrorDialogOpen(true);
        setSelected(currentValue);
      }
    }
  });

  const onSave = () => {
    doSetSourceOfTruth({saas: selected === automaticValue ? null : selected});
  };

  return <>
    {errorDialog}
    <DefaultTableContainer shouldUseTableContainer={false}>
      <Box sx={{padding: theme.spacing(2)}}>
        <Typography variant="h6" sx={{fontWeight: 'bold'}}>
          Source of truth
        </Typography>
        <Typography sx={{color: theme.palette.text.secondary, marginBottom: theme.spacing(2)}}>
          Ghost users are users without an active account in the source of truth.
          Choose one of your connected SaaSes, or {automaticLabel} to let Detexian
          choose one, preferring HR systems.
        </Typography>
        <Typography sx={{marginBottom: theme.spacing(2)}}>
          Current: <strong data-testid="current-source-of-truth">{currentLabel}</strong>
        </Typography>
        <Box sx={{display: 'flex', alignItems: 'center', gap: theme.spacing(2)}}>
          <FormControl size="small" sx={{minWidth: '300px'}}>
            <InputLabel id="source-of-truth-label">Source of truth</InputLabel>
            <Select
              labelId="source-of-truth-label"
              label="Source of truth"
              value={selected}
              displayEmpty
              disabled={isSaving}
              renderValue={(value) => value === automaticValue ?
                automaticLabel :
                options.find((o) => o.identifier === value)?.name ?? value}
              onChange={(event) => setSelected(event.target.value)}
            >
              <MenuItem value={automaticValue}>{automaticLabel}</MenuItem>
              {options.map((o) =>
                <MenuItem key={o.identifier} value={o.identifier}>{o.name}</MenuItem>
              )}
            </Select>
          </FormControl>
          <DefaultButton
            variant="contained"
            disabled={isSaving || selected === currentValue}
            onClick={onSave}
          >
            Save
          </DefaultButton>
          {isSaving && <CircularProgress size={20}/>}
        </Box>
      </Box>
    </DefaultTableContainer>
  </>;
};
